import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  MetricCard,
  PageHeader,
  Select,
  SkeletonRows,
  Table,
  useToast,
  type BadgeTone,
  type Column,
} from "../components/ui";
import {
  billingService,
  type AvailablePlan,
  type BillingPeriod,
  type CreditEntry,
  type SubscriptionOverview,
} from "../services/billing.service";
import { formatDateTime, formatMoney, formatNumber } from "../lib/format";


const statusTones: Record<string, BadgeTone> = {
  trial: "info",
  active: "ok",
  past_due: "warn",
  cancelled: "neutral",
  expired: "danger",
};

const typeLabels: Record<string, string> = {
  credit: "Entrada",
  debit: "Consumo",
  adjustment: "Ajuste",
  refund: "Estorno",
};

export default function BillingPage() {
  const toast = useToast();
  const [overview, setOverview] = useState<SubscriptionOverview | null>(null);
  const [entries, setEntries] = useState<CreditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<BillingPeriod>("monthly");
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subscription, credits] = await Promise.all([
        billingService.subscription(),
        billingService.credits(),
      ]);
      setOverview(subscription);
      setEntries(credits.data);
    } catch {
      toast.error("Não foi possível carregar a assinatura.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function subscribe(option: AvailablePlan) {
    setSubscribing(option.plan.id);
    try {
      const response = await billingService.subscribe({ plan: option.plan.id, billing_period: period });
      if (response.meta.payment_pending) {
        toast.notify("Assinatura criada. A cobrança automática ainda depende da integração de pagamento.");
      } else {
        toast.success("Assinatura ativada.");
      }
      await load();
    } catch {
      toast.error("Não foi possível contratar este plano.");
    } finally {
      setSubscribing(null);
    }
  }

  async function cancel() {
    const current = overview?.data;
    if (!current) return;
    try {
      await billingService.cancel(current.id);
      toast.success("Assinatura cancelada.");
      await load();
    } catch {
      toast.error("Não foi possível cancelar.");
    } finally {
      setCancelling(false);
    }
  }

  const current = overview?.data ?? null;
  const usage = overview?.meta.usage ?? {};

  const columns: Column<CreditEntry>[] = [
    { key: "date", header: "Data", render: (row) => formatDateTime(row.created_at) },
    { key: "type", header: "Tipo", render: (row) => typeLabels[row.type] ?? row.type },
    { key: "source", header: "Origem", hideOnMobile: true, render: (row) => row.source },
    {
      key: "description",
      header: "Descrição",
      hideOnMobile: true,
      render: (row) => row.description ?? "—",
    },
    {
      key: "amount",
      header: "Créditos",
      align: "right",
      render: (row) => (
        <span className={row.type === "debit" ? "text-danger" : "text-ink"}>
          {row.type === "debit" ? "-" : "+"}
          {formatNumber(row.amount)}
        </span>
      ),
    },
    { key: "balance", header: "Saldo", align: "right", render: (row) => formatNumber(row.balance_after) },
  ];

  return (
    <>
      <PageHeader
        title="Assinatura"
        description="Plano contratado, limites de uso e extrato de créditos da sua organização."
        actions={
          <Select
            value={period}
            onChange={(event) => setPeriod(event.target.value as BillingPeriod)}
            options={[
              { value: "monthly", label: "Cobrança mensal" },
              { value: "yearly", label: "Cobrança anual" },
            ]}
          />
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard
          label="Saldo de créditos"
          value={formatNumber(overview?.meta.credit_balance ?? 0)}
          loading={loading}
        />
        <MetricCard
          label="Projetos"
          value={`${usage.projects?.used ?? 0} / ${usage.projects?.limit ?? "∞"}`}
          loading={loading}
        />
        <MetricCard
          label="IA no mês"
          value={`${usage.ai_runs?.used ?? 0} / ${usage.ai_runs?.limit ?? "∞"}`}
          loading={loading}
        />
        <MetricCard
          label="Publicações no mês"
          value={`${usage.deploys?.used ?? 0} / ${usage.deploys?.limit ?? "∞"}`}
          loading={loading}
        />
      </div>

      <Card
        title="Plano atual"
        action={
          current ? (
            <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>
              Cancelar assinatura
            </Button>
          ) : undefined
        }
      >
        {loading ? (
          <SkeletonRows rows={2} />
        ) : current ? (
          <div className="space-y-2 text-sm text-ink-muted">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-base font-medium text-ink">{current.plan?.name}</span>
              <Badge tone={statusTones[current.status] ?? "neutral"}>{current.status_label ?? current.status}</Badge>
            </div>
            <p>
              {formatMoney(current.price_cents)} ·{" "}
              {current.billing_period === "yearly" ? "cobrança anual" : "cobrança mensal"}
            </p>
            {current.trial_ends_at && <p>Período de teste até {formatDateTime(current.trial_ends_at)}.</p>}
            {current.current_period_end && <p>Ciclo atual até {formatDateTime(current.current_period_end)}.</p>}
          </div>
        ) : (
          <EmptyState
            title="Nenhum plano contratado"
            description="Escolha um dos planos disponíveis abaixo para liberar os limites de uso."
          />
        )}
      </Card>

      <Card title="Planos disponíveis" subtitle="Somente planos liberados para a sua organização.">
        {loading ? (
          <SkeletonRows rows={2} />
        ) : (overview?.meta.available_plans.length ?? 0) === 0 ? (
          <EmptyState
            title="Nenhum plano liberado"
            description="Fale com quem administra a sua conta para liberar um plano."
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {overview?.meta.available_plans.map((option) => (
              <div key={option.plan.id} className="rounded-lg border border-line/60 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-ink">{option.plan.name}</p>
                    <p className="mt-1 text-xs text-ink-muted">{option.plan.description}</p>
                  </div>
                  {option.plan.is_mandatory && <Badge tone="info">Obrigatório</Badge>}
                </div>
                <p className="mt-3 text-lg font-semibold text-ink">
                  {formatMoney(period === "yearly" ? option.yearly_price_cents : option.monthly_price_cents)}
                </p>
                <ul className="mt-2 space-y-1 text-xs text-ink-muted">
                  <li>{option.plan.limits.projects ?? "∞"} projetos</li>
                  <li>{option.plan.limits.ai_runs ?? "∞"} execuções de IA por mês</li>
                  <li>{option.plan.limits.credits ?? "∞"} créditos por mês</li>
                </ul>
                <Button
                  className="mt-4 w-full"
                  size="sm"
                  loading={subscribing === option.plan.id}
                  onClick={() => subscribe(option)}
                >
                  Contratar
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Extrato de créditos" subtitle="Cada entrada e cada consumo, com origem registrada.">
        {loading ? (
          <SkeletonRows rows={4} />
        ) : (
          <Table
            columns={columns}
            rows={entries}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Sem movimentações"
                description="As entradas e consumos de créditos aparecem aqui."
              />
            }
          />
        )}
      </Card>

      <ConfirmDialog
        open={cancelling}
        title="Cancelar assinatura"
        description="O acesso continua até o fim do ciclo já pago."
        confirmLabel="Cancelar assinatura"
        destructive
        onConfirm={cancel}
        onCancel={() => setCancelling(false)}
      />
    </>
  );
}
